import { Injectable } from '@angular/core';
import { PropertyStateService } from '../state/property-state.service';
import { OfferRentalCalculatorService } from './offer-rental-calculator.service';
import { CalculatorService } from './calculator.service';

@Injectable()
export class AARentalService {

    constructor(
        private state: PropertyStateService,
        private calculator: CalculatorService,
        private offerRentalCalculator: OfferRentalCalculatorService,
    ) {}

    init() {
        this.calculator.setFieldValuesFromParseObject([this.state.aaRental_cashBox, this.state.aaRental_financeBox], this.state.aaRental);
    }

    calculate() {
        this.updateTopBoxValues();
        this.updateCashBoxValues();
        this.updateFinanceBoxValues();
    }

    updateTopBoxValues() {
        const state = this.state;

        this.calculator.getField(state.aaRental_sharedTopBox, 'purchase-price').outputValue = this.offerRentalCalculator.offerPrice;
        this.calculator.getField(state.aaRental_sharedTopBox, 'commission').outputValue = state.offerRental.get('commission');
        this.calculator.getField(state.aaRental_sharedTopBox, 'arv').outputValue = state.offerRental.get('arv');
    }

    updateCashBoxValues() {
        const state = this.state;

        this.calculator.getField(state.aaRental_cashBox, 'closing-cost').outputValue = this.calculateClosingCost();
        this.calculator.getField(state.aaRental_cashBox, 'reno-cost').outputValue = state.offerRental.get('renoCost');
        this.calculator.getField(state.aaRental_cashBox, 'acqui-cost').outputValue = this.calculateAcquiCost();

        this.calculator.getField(state.aaRental_cashBox, 'rent').outputValue = state.offerRental.get('rentalRate');
        this.calculator.getField(state.aaRental_cashBox, 'gross-income').outputValue = this.calculateGrossIncome();
        this.calculator.getField(state.aaRental_cashBox, 'return').outputValue = this.calculateReturn();
        this.calculator.getField(state.aaRental_cashBox, 'man-fee').outputValue = this.offerRentalCalculator.managementFee;
        this.calculator.getField(state.aaRental_cashBox, 'less-vacancy').outputValue = this.calculateLessVacancy();
        this.calculator.getField(state.aaRental_cashBox, 'less-taxes').outputValue = state.offerRental.get('propertyTaxes');

        this.calculator.getField(state.aaRental_cashBox, 'less-insurance').outputValue = state.offerRental.get('insurance') ? state.offerRental.get('insurance') : (state.offerRental.get('arv') * 0.00035) * 12 ;
        this.calculator.getField(state.aaRental_cashBox, 'less-hoa').outputValue = this.calculateLessHOA();
        this.calculator.getField(state.aaRental_cashBox, 'total-expenses').outputValue = this.calculateTotalExpenses();

        this.calculator.getField(state.aaRental_cashBox, 'net-income').outputValue = this.calculateNetIncome();
        this.calculator.getField(state.aaRental_cashBox, 'cash-flow').outputValue = this.calculateCashFlow();
        this.calculator.getField(state.aaRental_cashBox, 'cap-rate').outputValue = this.calculateCapRate();
        this.calculator.getField(state.aaRental_cashBox, 'plus-equity').outputValue = this.calculatePlusEquity();
    }

    updateFinanceBoxValues() {
        const state = this.state;

        this.calculator.getField(state.aaRental_financeBox, 'down-payment').outputValue = this.calculateDownPayment();
        this.calculator.getField(state.aaRental_financeBox, 'amount-financed').outputValue = this.calculateAmountFinanced();
        this.calculator.getField(state.aaRental_financeBox, 'points-paid').outputValue = this.calculateLoanPointsPaid();
        this.calculator.getField(state.aaRental_financeBox, 'annual-loan-payment').outputValue = this.calculateAnnualLoanPayment();
        this.calculator.getField(state.aaRental_financeBox, 'monthly-loan-payment').outputValue = this.calculateMonthlyLoanPayment();
        this.calculator.getField(state.aaRental_financeBox, 'total-payment').outputValue = this.calculateTotalPayment();
        this.calculator.getField(state.aaRental_financeBox, 'annual-net').outputValue = this.calculateAnnualNet();
        this.calculator.getField(state.aaRental_financeBox, 'cash-flow').outputValue = this.calculateNetCashFlow();

        this.calculator.getField(state.aaRental_financeBox, 'cash-out-of-pocket').outputValue = this.calculateTotalCashOutOfPocket();
        this.calculator.getField(state.aaRental_financeBox, 'cash-on-cash').outputValue = this.calculateCashOnCash();
    }

    calculateReturn(): number {
        return this.calculateGrossIncome() / this.calculateAcquiCost();
    }

    calculatePlusEquity(): number {
        return this.state.offerRental.get('arv') - this.calculateAcquiCost();
    }

    calculateGrossIncome(): number {
        return 12 * this.state.offerRental.get('rentalRate');
    }

    calculateLessHOA(): number {
        return 12 * this.state.offerRental.get('hoa');
    }

    calculateLessVacancy(): number {
        return 0.1 * this.calculateGrossIncome();
    }

    calculateTotalExpenses(): number {
        const state = this.state;

        const manFee = this.offerRentalCalculator.managementFee;
        const manReserve = this.calculator.getField(state.aaRental_cashBox, 'man-reserve').inputValue || 0;
        const vacancy = this.calculateLessVacancy();
        const pest = this.calculator.getField(state.aaRental_cashBox, 'less-pest').inputValue || 0;
        const taxes = state.offerRental.get('propertyTaxes');
        const insurance = state.offerRental.get('insurance') ? state.offerRental.get('insurance') : (state.offerRental.get('arv') * 0.00035) * 12;
        const hoa = this.calculateLessHOA();

        return manFee + manReserve + vacancy + pest + taxes + insurance + hoa;
    }

    calculateNetIncome(): number {
        return this.calculateGrossIncome() - this.calculateTotalExpenses();
    }

    calculateCashFlow(): number {
        return this.calculateNetIncome() / 12;
    }

    calculateClosingCost(): number {
        return this.offerRentalCalculator.offerPrice * this.state.offerRental.get('closingCost');
    }

    calculateAcquiCost(): number {
        const state = this.state;

        const purchasePrice = this.calculator.getField(state.aaRental_sharedTopBox, 'purchase-price').outputValue as number;
        const closingCost = this.calculateClosingCost();
        const renoCost = state.offerRental.get('renoCost');

        return purchasePrice + closingCost + renoCost;
    }

    calculateCapRate(): number {
        return this.calculateNetIncome() / this.calculateAcquiCost();
    }

    calculateDownPayment() {
        // Require these values to be set for calculation
        if (
            this.calculator.isInputUnset(this.calculator.getField(this.state.aaRental_financeBox, 'down-payment').inputValue) ||
            this.calculator.isInputUnset(this.offerRentalCalculator.offerPrice)
        ) {
            return null;
        }

        return (this.calculator.getField(this.state.aaRental_financeBox, 'down-payment').inputValue as number) * this.offerRentalCalculator.offerPrice;
    }

    calculateAmountFinanced() {
        // Require these values to be set for calculation
        if (
            this.calculator.isInputUnset(this.calculator.getField(this.state.aaRental_financeBox, 'down-payment').inputValue) ||
            this.calculator.isInputUnset(this.offerRentalCalculator.offerPrice)
        ) {
            return null;
        }

        return (1 - (this.calculator.getField(this.state.aaRental_financeBox, 'down-payment').inputValue as number)) * this.offerRentalCalculator.offerPrice;
    }

    calculateLoanPointsPaid() {
        const amountFinanced = this.calculateAmountFinanced() as number;
        const points = this.calculator.getField(this.state.aaRental_financeBox, 'points').inputValue as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(amountFinanced) || this.calculator.isInputUnset(points)) {
            return null;
        }

        return amountFinanced * points;
    }

    calculateAnnualLoanPayment() {
        const amountFinanced = this.calculateAmountFinanced() as number;
        const loanRate = this.calculator.getField(this.state.aaRental_financeBox, 'loan-rate').inputValue as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(amountFinanced) || this.calculator.isInputUnset(loanRate)) {
            return null;
        }

        return amountFinanced * loanRate;
    }

    calculateMonthlyLoanPayment() {
        const annual = this.calculateAnnualLoanPayment() as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(annual)) {
            return null;
        }

        return annual / 12;
    }

    calculateTotalPayment() {
        const annualLoan = this.calculateAnnualLoanPayment() as number;
        const pointsPaid = this.calculateLoanPointsPaid() as number;
        const annualExpenses = this.calculateTotalExpenses() as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(annualLoan) || this.calculator.isInputUnset(pointsPaid) || this.calculator.isInputUnset(annualExpenses)) {
            return null;
        }

        return annualLoan + pointsPaid + annualExpenses;
    }

    calculateAnnualNet() {
        const gross = this.calculateGrossIncome();
        const total = this.calculateTotalPayment() as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(gross) || this.calculator.isInputUnset(total)) {
            return null;
        }

        return gross - total;
    }

    calculateNetCashFlow() {
        const net = this.calculateAnnualNet() as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(net)) {
            return null;
        }

        return net / 12;
    }

    calculateTotalCashOutOfPocket() {
        const renoCost = this.state.offerRental.get('renoCost');
        const downPayment = this.calculateDownPayment();
        const totalExpenses = this.calculateTotalExpenses();
        const closingcost = this.calculateClosingCost();

        // Require these values to be set for calculation
        if (
            this.calculator.isInputUnset(renoCost) ||
            this.calculator.isInputUnset(downPayment) ||
            this.calculator.isInputUnset(totalExpenses) ||
            this.calculator.isInputUnset(closingcost)
        ) {
            return null;
        }

        return renoCost + downPayment + totalExpenses + closingcost;
    }

    calculateCashOnCash() {
        const net = this.calculateAnnualNet() as number;
        const outOfPockit = this.calculateTotalCashOutOfPocket();

        // Require these values to be set for calculation
        if (
            this.calculator.isInputUnset(net) ||
            this.calculator.isInputUnset(outOfPockit)
        ) {
            return null;
        }

        return net / outOfPockit;
    }
}

